import React, {forwardRef} from 'react';

import {cn} from '../lib/utils';

export type CardProps = React.HTMLAttributes<HTMLDivElement>;

/*
 * Six neutral structural containers. Every one renders a plain `div`: none
 * renders a heading element or sets a heading, `region`, or any other landmark
 * role, and none carries product data or application layout behaviour. A
 * consumer that needs a heading level or a landmark supplies it itself, so the
 * composition never invents document semantics on their behalf.
 */

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card({className, ...props}, ref) {
  return (
    <div
      {...props}
      data-slot="card"
      className={cn(
        'rounded-md border border-(--compass-border) bg-(--compass-surface) text-(--compass-on-surface)',
        className
      )}
      ref={ref}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, CardProps>(function CardHeader(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="card-header"
      className={cn('flex flex-col gap-1 p-4', className)}
      ref={ref}
    />
  );
});

export const CardTitle = forwardRef<HTMLDivElement, CardProps>(function CardTitle(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="card-title"
      className={cn('text-base font-semibold', className)}
      ref={ref}
    />
  );
});

export const CardDescription = forwardRef<HTMLDivElement, CardProps>(function CardDescription(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="card-description"
      className={cn('text-sm text-(--compass-on-muted)', className)}
      ref={ref}
    />
  );
});

export const CardContent = forwardRef<HTMLDivElement, CardProps>(function CardContent(
  {className, ...props},
  ref
) {
  return (
    <div {...props} data-slot="card-content" className={cn('p-4 pt-0', className)} ref={ref} />
  );
});

export const CardFooter = forwardRef<HTMLDivElement, CardProps>(function CardFooter(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="card-footer"
      className={cn('flex items-center p-4 pt-0', className)}
      ref={ref}
    />
  );
});
